import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import BackdropComponent from "../../Spinner/Spinner";
import classes from "./TicketEquipmentV2.module.css";
import {
  getIDMTicketDetails,
  getAgentCommentsByRosterId,
  getTraineeITAMStatusByRosterId,
  refreshITAMStatus,
  getEmployeeADStatusByOracleId,
} from "../../../services/userServices";
import SnackBar from "components/SnackBar/SnackBar";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  IconButton,
  Typography,
  Grid,
  Tooltip,
  tooltipClasses,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import DisabledByDefaultOutlinedIcon from "@mui/icons-material/DisabledByDefaultOutlined";
import CheckBoxOutlineBlankOutlinedIcon from "@mui/icons-material/CheckBoxOutlineBlankOutlined";
import PowerSharpIcon from "@mui/icons-material/PowerSharp";
import SyncIcon from "@mui/icons-material/Sync";
import PropTypes from "prop-types";
import applicationUsageLog from "../../../utils/applicationUsagesLog";
import LastUpdateTooltip from "../Helpers/LastUpdateTooltip";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

const getFilteredData = (data, property, value) =>
  data.filter((itm) => itm[property] === value);

const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 600,
    fontSize: "10px",
    backgroundColor: "white",
    color: "black",
  },
});

const TicketEquipmentV2 = (props) => {
  const [loading, setLoading] = useState(false);
  const initialRender = useRef(true);
  const classTraineeData = useSelector((state) => state.bulkEdit);
  const [idmTicketData, setIdmTicketData] = useState([]);
  const [informaticaData, setInformaticaData] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [agentComments, setAgentComments] = useState([]);
  const [traineeItemData, setTraineeItemData] = useState([]);
  const [rows, setRows] = useState([]);
  const [order, setOrder] = useState("ASC");
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [totalActiveCount, setTotalActiveCount] = useState({
    total: 0,
    active: 0,
  });
  const rosterId = classTraineeData?.traineeDetails[0]?.classRosterId;

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        await fetchData();

        if (!cancelled) {
          applicationUsageLog(
            localStorage.getItem("User"),
            localStorage.getItem("EmailId"),
            "ASTT",
            "Ticket Equipment",
          );
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to initialize:", error);
        }
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, []);

  // Helper function - extracted outside the component or at component level
  const getTTECRegistrationStatus = (oracleId, agentComments) => {
    const agentComment = agentComments.find(
      (value) => value.OracleId === oracleId,
    );
    return agentComment?.TTECRegistrationStatus || null;
  };

  // Helper function - creates a single row object
  const createRowObject = (item, index, agentComments, traineeItemData) => {
    return {
      id: `${item.oracleId}-${index}`,
      oracleId: item.oracleId,
      agentComments: item.oracleId,
      ttecRegistrationStatus: getTTECRegistrationStatus(
        item.oracleId,
        agentComments,
      ),
      itamAsset: prepareITAMData(item.oracleId, traineeItemData),
      employeeName: `${item.preferredFirstName} ${item.preferredLastName}`,
      ritmOId: item.oracleId,
      adOId: item.oracleId,
      adStatusOID: item.oracleId,
      itDepotOID: item.oracleId,
      logisticsOID: "",
      traineeStatus: item.traineeStatus,
    };
  };

  // Helper function - prepares all row data
  const prepareRowData = (classTraineeData, agentComments, traineeItemData) => {
    return (
      classTraineeData?.traineeDetails.map((item, index) =>
        createRowObject(item, index, agentComments, traineeItemData),
      ) || []
    );
  };

  // Helper function - updates state with new row data
  const updateRowState = (rowData, setRows, setFilterData, initialCheck) => {
    setRows(rowData);
    setFilterData(rowData);
    initialCheck(rowData);
  };

  // Refactored useEffect
  useEffect(() => {
    if (agentComments && agentComments.length > 0) {
      const rowData = prepareRowData(
        classTraineeData,
        agentComments,
        traineeItemData,
      );
      updateRowState(rowData, setRows, setFilterData, initialCheck);
    }
  }, [agentComments, traineeItemData, classTraineeData]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    } else {
      setLoading(true);

      const timeout = setTimeout(() => {
        filtering(props.filterStatus, filterData);
        setLoading(false);
      }, 1000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [props.filterStatus]);

  const filtering = async (filter, data) => {
    let filterData = [];
    if (filter === "ALL") {
      filterData = data;
    } else {
      filterData = data.filter(
        (item) => item.traineeStatus === props.filterStatus,
      );
    }
    setRows(filterData);
    initialCheck(filterData);
    props.closeLoader();
  };

  const initialCheck = (data) => {
    let activeCount = getFilteredData(data, "traineeStatus", "Active");
    setTotalActiveCount({
      total: data.length,
      active: activeCount.length,
    });
  };

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const fetchAgentComments = async () => {
    setLoading(true);

    const agentCommentsResponse = await getAgentCommentsByRosterId(rosterId);
    let agentCommentsData = [];
    if (agentCommentsResponse.message === "Success") {
      agentCommentsData = agentCommentsResponse.data;
      setAgentComments(agentCommentsData);
    } else {
      setSnack({
        open: true,
        message: "Failed to load agent comments.",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const prepareRefreshITAMDataPayload = () => {
    const oracleIds = rows.map((item) => {
      return {
        OracleId: item?.oracleId,
      };
    });
    const payload = {
      classRosterId: rosterId,
      oracleIds: oracleIds,
      modifiedBy: localStorage.getItem("User"),
    };
    return payload;
  };

  const fetchITAMStatus = async () => {
    setLoading(true);
    const response = await getTraineeITAMStatusByRosterId(rosterId);
    let ITAMStatusData = [];
    if (response.message === "Success") {
      ITAMStatusData = response.data;
      setTraineeItemData(ITAMStatusData);
    } else if (response.statusCode === 404) {
      setSnack({
        open: true,
        message: "No ITAM Data found.",
        severity: "info",
      });
    } else {
      setSnack({
        open: true,
        message: "Failed to load ITAM status.",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const refreshITAMData = async () => {
    setLoading(true);
    const payload = prepareRefreshITAMDataPayload();
    const response = await refreshITAMStatus(payload);
    let ITAMStatusData = [];
    if (response.message === "Success") {
      ITAMStatusData = response.data;
      setTraineeItemData(ITAMStatusData);
      setSnack({
        open: true,
        message: "ITAM Data refreshed",
        severity: "success",
      });
    } else if (response.statusCode === 404) {
      setSnack({
        open: true,
        message: "No ITAM Data found.",
        severity: "info",
      });
    } else {
      setSnack({
        open: true,
        message: "Failed to load ITAM data.",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const prepareITAMData = (oracleId, data) => {
    if (data && data.length > 0) {
      const filteredData = data.filter((item) => item?.OracleId === oracleId);
      return filteredData.map((item) => ({
        assetName: item?.Name,
        assignedDate: item?.AssignedDate,
      }));
    }
    return [];
  };

  const fetchData = async () => {
    setLoading(true);
    let oracleIds = classTraineeData?.traineeDetails.map(
      (item) => item.oracleId,
    );

    let informticaPayload = {
      oracle_id: oracleIds.toString(),
    };

    const ritmResponse = await getIDMTicketDetails(rosterId);
    if (ritmResponse.message === "Success") {
      setIdmTicketData(ritmResponse.data);
    } else {
      setSnack({
        open: true,
        message: "Failed to load data!",
        severity: "error",
      });
    }

    const employeeADStatusResponse = await getEmployeeADStatusByOracleId(
      oracleIds.join(","), 0
    );
    if (employeeADStatusResponse.statusCode === 200) {
      setInformaticaData(employeeADStatusResponse.data);
    } else {
      setSnack({
        open: true,
        message: "Failed to load data!",
        severity: "error",
      });
    }
    await fetchAgentComments();
    await fetchITAMStatus();
    setLoading(false);
  };

  const createPopOver = (content) => {
    const { assignmentGroup, createdOn, requestedOn, taskStatus } = content;
    return (
      <div>
        <Typography sx={{ p: 1 }}>Created On : {createdOn} </Typography>
        <Typography sx={{ p: 1 }}> Requested On : {requestedOn}</Typography>
        <Typography sx={{ p: 1 }}>
          Assigned group : {assignmentGroup}
        </Typography>
        <Typography sx={{ p: 1 }}> Status : {taskStatus}</Typography>
      </div>
    );
  };

  const getIdmContent = (header, oracleId) => {
    let idmData = idmTicketData.filter(
      (value) => value.oracleId.toString() === oracleId.toString(),
    );
    let ticketInfo = idmData.filter(
      (value) =>
        value.idmTicketNumber != null && value.idmTicketResponse != null,
    );
    let ritmDetails = ticketInfo.map((value) => value.idmTicketResponse.data);
    let tasksData = ritmDetails.filter((value) => value.tasks != null);
    let groupContent = tasksData.map((ritmData) => {
      let returnObject = ritmData.tasks.map((taskData) => {
        return {
          taskNumber: taskData.number,
          assignmentGroup: taskData.assignment_group,
          ritmNumber: ritmData.number,
          createdOn: ritmData.created_on,
          requestedOn: ritmData.requested_date,
          ritmStatus: ritmData.state,
          taskStatus: taskData.state,
        };
      });
      return returnObject;
    });
    groupContent = groupContent.flat();
    let getAssignmentGroupData = (group) => {
      if (group === "depot") {
        return groupContent != null && groupContent.length > 0
          ? groupContent.filter((value) =>
              value.assignmentGroup.toLowerCase().includes(group),
            )
          : [];
      }
      return groupContent != null && groupContent.length > 0
        ? groupContent.filter(
            (value) => value.assignmentGroup.toLowerCase() === group,
          )
        : [];
    };

    let content = <div></div>;
    if (header === "RITM") {
      let ticketInfo = idmData.filter((value) => value.idmTicketNumber != null);
      let ritmNumber = ticketInfo.map((value) => value.idmTicketNumber);
      content = (
        <div key={ritmNumber}>
          <Tooltip title={ritmNumber.join(", ")}>
            <span>{ritmNumber.join(", ")}</span>
          </Tooltip>
        </div>
      );
    } else if (header === "AD") {
      let contentData = getAssignmentGroupData("isu provisioning");
      content = contentData.map((value) => {
        return (
          <div key={value.taskNumber}>
            <CustomWidthTooltip title={createPopOver(value)}>
              <div className={classes.taskContainer}>
                {getIcon(value.taskStatus)} {value.taskNumber}
              </div>
            </CustomWidthTooltip>
          </div>
        );
      });
    } else if (header === "ITDepot") {
      let contentData = getAssignmentGroupData("depot");
      content = contentData.map((value) => (
        <div key={value.taskNumber}>
          <CustomWidthTooltip title={createPopOver(value)}>
            <div className={classes.taskContainer}>
              {getIcon(value.taskStatus)} {value.taskNumber}
            </div>
          </CustomWidthTooltip>
        </div>
      ));
    }

    return content;
  };

  const getIcon = (ticketState) => {
    ticketState = ticketState.replace(/ /g, "");
    if (ticketState.toLowerCase() === "closedcomplete") {
      return (
        <CheckBoxOutlinedIcon fontSize="small" sx={{ color: "#00A300" }} />
      );
    } else if (
      ticketState.toLowerCase() === "closedincomplete" ||
      ticketState.toLowerCase() === "cancelled"
    ) {
      return (
        <DisabledByDefaultOutlinedIcon
          fontSize="small"
          sx={{ color: "#FF0000" }}
        />
      );
    }
    return (
      <CheckBoxOutlineBlankOutlinedIcon
        fontSize="small"
        sx={{ color: "#000000" }}
      />
    );
  };

  const getADStatus = (oracleId) => {
    let adStatus = informaticaData?.find(
      (value) => value.oracleId === oracleId,
    );
    if (adStatus !== undefined) {
      return <>{adStatus?.activeInAD === true ? "Active" : "In-Active"}</>;
    }
    return <></>;
  };

  const renderITAMCellPopOverContent = (data) => {
    return (
      <Grid container direction="column" spacing={1}>
        <Grid container item>
          <Grid item xs={7}>
            <Typography variant="body1" className={classes.headerTextItam}>
              Asset name
            </Typography>
          </Grid>
          <Grid item xs={5}>
            <Typography variant="body1" className={classes.headerTextItam}>
              Assigned date
            </Typography>
          </Grid>
        </Grid>

        {/* Data Rows */}
        {data.map((item) => (
          <Grid container item key={item?.assetName}>
            <Grid item xs={7}>
              <Typography variant="body2">{item?.assetName}</Typography>
            </Grid>
            <Grid item xs={5}>
              <Typography variant="body2">{item?.assignedDate}</Typography>
            </Grid>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderITAMCellContent = (data) => {
    if (data.length > 1) {
      return (
        <b>
          <i>Asset details</i>
        </b>
      );
    }
    const item = data[0];
    return (
      <Grid container>
        <Grid item xs={12}>
          {item?.assetName}
        </Grid>
        <Grid item xs={12}>
          {item?.assignedDate}
        </Grid>
      </Grid>
    );
  };

  const renderITAMCell = (data) => {
    if (data && data.length > 0)
      return (
        <CustomWidthTooltip title={renderITAMCellPopOverContent(data)}>
          <Box>{renderITAMCellContent(data)}</Box>
        </CustomWidthTooltip>
      );
    return <></>;
  };

  const sorting = (col) => {
    if (order === "ASC") {
      const sorted = [...rows].sort((a, b) => {
        if (a[col]?.toLowerCase() === b[col]?.toLowerCase()) return 0;
        return a[col]?.toLowerCase() > b[col]?.toLowerCase() ? 1 : -1;
      });
      setRows(sorted);
      setOrder("DSC");
    }
    if (order === "DSC") {
      const sorted = [...rows].sort((a, b) => {
        if (a[col]?.toLowerCase() === b[col]?.toLowerCase()) return 0;
        return a[col]?.toLowerCase() < b[col]?.toLowerCase() ? 1 : -1;
      });
      setRows(sorted);
      setOrder("ASC");
    }
  };

  const getAgentComments = (oracleId) => {
    const comments = agentComments.filter(
      (value) => value.OracleId === oracleId,
    );
    return comments[0]?.EmployeeComment ?? "";
  };

  const renderTextCell = (nameWithId, traineeStatus, isInactive) => (
    <Grid item xs={0.6} align="center">
      <p className={isInactive ? "text-decoration-line-bulk" : null}>
        {nameWithId}
      </p>
      {isInactive && <p className={classes.blueColor}>{traineeStatus}</p>}
    </Grid>
  );

  return (
    <div className={classes.container}>
      {loading ? <BackdropComponent /> : null}
      <div className={classes.gridContainer}>
        <TableContainer className={classes.container} component={Paper}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                <TableCell
                  className={"text-center align-center"}
                  align="center"
                  sx={{
                    fontFamily: "Urbanist",
                    fontWeight: "bold",
                    fontSize: "16px",
                    lineHeight: "24px",
                    letterSpacing: "0%",
                    backgroundColor: "#e5e4e2",
                    color: "#3F5765",
                    minWidth: "300px !important",
                    position: "sticky",
                    left: 0,
                    top: "0 !important",
                    zIndex: "1100 !important",
                    boxShadow: "2px 0 5px -2px rgba(0,0,0,0.15)",
                  }}
                  onClick={() => sorting("employeeName")}
                >
                  <span>Name/Oracle Id</span>
                  <Box sx={{ display: "inline-block" }}>
                    <ArrowUpwardIcon
                      className={`${classes.arrowIcon} ${
                        order === "ASC"
                          ? classes.arrowAsc
                          : classes.arrowInactive
                      }`}
                    />
                    <ArrowDownwardIcon
                      className={`${classes.arrowIcon} ${
                        order === "DSC"
                          ? classes.arrowDesc
                          : classes.arrowInactive
                      }`}
                    />
                  </Box>
                  <Typography
                    className={classes.blueColor}
                    sx={{ fontSize: "14px", margin: "0" }}
                  >
                    Total: {totalActiveCount.total} | Active:{" "}
                    {totalActiveCount.active}
                  </Typography>
                </TableCell>

                <TableCell
                  className={`${classes.tableHeaderCell} text-center align-center`}
                >
                  IDM Ticket
                </TableCell>
                <TableCell
                  className={`${classes.tableHeaderCell} text-center align-center`}
                >
                  AD
                </TableCell>
                <TableCell
                  className={`${classes.tableHeaderCell} text-center align-center`}
                >
                  AD Status
                </TableCell>
                <TableCell
                  className={`${classes.tableHeaderCell} text-center align-center`}
                >
                  IT Depot
                </TableCell>
                <TableCell
                  className={`${classes.tableHeaderCell} text-center align-center`}
                >
                  ITAM
                  <IconButton
                    aria-label="Refresh"
                    sx={{ color: "#3299FF", ml: 1 }}
                    title="Refresh ITAM Status"
                    onClick={async () => {
                      await refreshITAMData();
                    }}
                  >
                    <SyncIcon />
                  </IconButton>
                </TableCell>
                <TableCell
                  className={`${classes.tableHeaderCell} text-center align-center`}
                >
                  Logistics
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell
                    align="center"
                    sx={{
                      position: "sticky",
                      left: 0,
                      backgroundColor: "#fff",
                      zIndex: 1,
                      boxShadow: "2px 0 5px -2px rgba(0,0,0,0.15)",
                    }}
                  >
                    {/* Individual Row Cells */}
                    <Grid
                      container
                      direction="row"
                      justifyContent="center"
                      columns={1}
                      alignItems="center"
                      spacing={0}
                    >
                      {renderTextCell(
                        `${row.employeeName} (${row.oracleId})`,
                        row.traineeStatus,
                        row.traineeStatus !== "Active",
                      )}
                      {row.ttecRegistrationStatus ? (
                        <Grid item xs={0.1}>
                          <Tooltip title="TTEConnect: Registered">
                            <PowerSharpIcon
                              placeholder="TTEConnect: Registered"
                              sx={{ color: "#69DE00" }}
                            />
                          </Tooltip>
                        </Grid>
                      ) : (
                        <Grid item xs={0.1}></Grid>
                      )}

                      <textarea
                        readOnly
                        className={classes.agentCommentInputContainer}
                        value={getAgentComments(row.oracleId)}
                        style={{ width: "100%", minWidth: "350px" }}
                      ></textarea>
                    </Grid>
                  </TableCell>
                  {/* Other Cells */}
                  <TableCell align="center">
                    {getIdmContent("RITM", row.ritmOId)}
                  </TableCell>
                  <TableCell align="center">
                    <div className={classes.taskListContainer}>
                      {getIdmContent("AD", row.adOId)}
                    </div>
                  </TableCell>
                  <TableCell align="center">
                    {getADStatus(row.adStatusOID)}
                  </TableCell>
                  <TableCell align="center">
                    <div className={classes.taskListContainer}>
                      {getIdmContent("ITDepot", row.itDepotOID)}
                    </div>
                  </TableCell>
                  <TableCell align="center">
                    {renderITAMCell(row.itamAsset)}
                  </TableCell>
                  <TableCell align="center">{row.logisticsOID}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
    </div>
  );
};

TicketEquipmentV2.propTypes = {
  filterStatus: PropTypes.string,
  closeLoader: PropTypes.func,
  resetStatus: PropTypes.bool,
  onChange: PropTypes.func,
};

export default TicketEquipmentV2;
